/**
 * Zod schemas of the DATA props of the claim usages that are not plain
 * shared cards (list, stakeholders, charges) — the `propsSchema` of their
 * registrations. The bound config props (`componentId`, `title`, `icon`) and
 * the injected `_layoutOverrides` are deliberately absent. Each schema is
 * checked against its TypeScript props with `satisfies`. Sample
 * `*_PREVIEW_PROPS` feed the admin config page's preview.
 */

import { z } from 'zod';

import type { ClaimChargesCardProps } from './ClaimChargesCard';
import type { ClaimsListCardProps } from './ClaimsListCard';
import type { ClaimStakeholdersCardProps } from './ClaimStakeholdersCard';

/** Props a page passes to the registered usages. */
export type ClaimsListCardDataProps = Pick<ClaimsListCardProps, 'rows' | 'loading' | 'error'>;
export type ClaimStakeholdersCardDataProps = Pick<
  ClaimStakeholdersCardProps,
  'rows' | 'loading' | 'error'
>;
export type ClaimChargesCardDataProps = Pick<ClaimChargesCardProps, 'rows' | 'loading' | 'error'>;

const loadingSchema = z.boolean().optional();
const errorSchema = z.string().nullable().optional();
const textSchema = z.string().nullable();
const amountSchema = z.number().nullable();

export const claimListRowSchema = z.object({
  id: z.string(),
  idSin: textSchema,
  assure: textSchema,
  produit: textSchema,
  numeroContrat: textSchema,
  reference: textSchema,
  dateSurvenance: z.union([z.number(), z.string()]).nullable(),
  circonstances: textSchema,
  coutAssureur: amountSchema,
  statut: textSchema,
});

export const claimsListCardDataSchema = z.object({
  rows: z.array(claimListRowSchema),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<ClaimsListCardDataProps>;

export const claimStakeholderRowSchema = z.object({
  id: z.string(),
  nature: textSchema,
  nomPrenom: textSchema,
  email: textSchema,
  telephone: textSchema,
});

export const claimStakeholdersCardDataSchema = z.object({
  rows: z.array(claimStakeholderRowSchema),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<ClaimStakeholdersCardDataProps>;

export const claimChargeRowSchema = z.object({
  id: z.string(),
  garantie: textSchema,
  provision: amountSchema,
  reglement: amountSchema,
  recoursEstime: amountSchema,
  recoursEncaisse: amountSchema,
  franchise: amountSchema,
});

export const claimChargesCardDataSchema = z.object({
  rows: z.array(claimChargeRowSchema),
  loading: loadingSchema,
  error: errorSchema,
}) satisfies z.ZodType<ClaimChargesCardDataProps>;

// ---------------------------------------------------------------------------
// Preview samples (admin config page) — fiche examples
// ---------------------------------------------------------------------------

export const CLAIMS_LIST_PREVIEW_PROPS: ClaimsListCardDataProps = {
  rows: [
    {
      id: '46081',
      idSin: '46081',
      assure: 'CCV BEAUMANOIR',
      produit: 'AUTOMOBILE',
      numeroContrat: '11096938S0001',
      reference: '26AU0317',
      dateSurvenance: 1787954400000,
      circonstances: 'Auto Matériel - Tiers Responsable',
      coutAssureur: null,
      statut: 'OUVERT',
    },
  ],
};

export const CLAIM_STAKEHOLDERS_PREVIEW_PROPS: ClaimStakeholdersCardDataProps = {
  rows: [
    {
      id: '0',
      nature: 'REPARATEUR',
      nomPrenom: '38 FORD BY MY CAR FONTAINE',
      email: 's.orsini@bymycar.fr',
      telephone: '04 76 26 99 99',
    },
  ],
};

export const CLAIM_CHARGES_PREVIEW_PROPS: ClaimChargesCardDataProps = {
  rows: [
    {
      id: '53015',
      garantie: 'Dommages Tous Accidents',
      provision: 1100,
      reglement: null,
      recoursEstime: null,
      recoursEncaisse: null,
      franchise: 1500,
    },
  ],
};

/** Sample values of the details card (fiche 22807 example, pre-formatted). */
export const CLAIM_DETAILS_PREVIEW_VALUES = {
  reference: '26AU0317',
  statut: 'En cours',
  produit: 'FLOTTE AUTOMOBILE (Autre)',
  dateSurvenance: '29/08/2026',
  dateDeclaration: '30/08/2026',
  type: 'MATÉRIEL',
  coutAssureur: '0,00 €',
  circonstances: 'Auto Matériel - Tiers Responsable',
  description: 'Tiers prend un stationnement et percuté Assuré en stationnement',
  dommages: 'portière gauche',
} as const;
